<script lang="ts" setup>
import { type VNodeChild, computed } from 'vue';
import {
  IconFile,
  IconLoader,
  IconSearch,
  IconAlertTriangleFilled,
  IconAlertOctagonFilled,
  IconCheck,
} from '@/icons';
import { hasSlotContent } from '../../utils';

type Preset = 'loading' | 'success' | 'warning' | 'error' | 'search';

interface PresetProps {
  // null in booleans type is needed because Vue set it to `false` by default
  // but we need to know if user set prop or not
  withBackground?: boolean | null;
  withIcon?: boolean | null;
  rounded?: boolean | null;
  backgroundColor?: string;
  iconBackgroundColor?: string;
  iconColor?: string;
}

interface Props extends PresetProps {
  preset?: Preset;
  size?: 'm' | 'l';
}

const { withBackground = null, withIcon = null, rounded = null, ...props } = defineProps<Props>();

defineSlots<{
  default?: () => VNodeChild;
  icon?: () => VNodeChild;
  text?: () => VNodeChild;
  content?: () => VNodeChild;
}>();

const presets: Record<Preset, PresetProps> = {
  loading: {
    withIcon: true,
    withBackground: true,
  },
  success: {
    backgroundColor: 'rgba(24, 165, 130, 0.08)',
    iconColor: '#007C5B',
    iconBackgroundColor: 'rgba(24, 165, 130, 0.12)',
    withIcon: true,
    withBackground: true,
  },
  warning: {
    backgroundColor: 'rgba(228, 161, 75, 0.08)',
    iconColor: '#C27D0D',
    iconBackgroundColor: 'rgba(228, 161, 75, 0.16)',
    withIcon: true,
    withBackground: true,
  },
  error: {
    backgroundColor: 'rgba(230, 57, 70, 0.08)',
    iconColor: '#CC1731',
    iconBackgroundColor: 'rgba(230, 57, 70, 0.12)',
    withIcon: true,
    withBackground: true,
  },
  search: {
    withIcon: true,
    withBackground: true,
  },
};

const preset = computed(() => {
  return props.preset ? presets[props.preset] : undefined;
});

const computedWithBackground = computed(() => {
  if (withBackground === null) {
    return preset.value?.withBackground ?? true;
  }

  return withBackground;
});

const computedWithIcon = computed(() => {
  if (withIcon === null) {
    return preset.value?.withIcon ?? true;
  }

  return withIcon;
});

const computedRounded = computed(() => {
  if (rounded === null) {
    return preset.value?.rounded ?? true;
  }

  return rounded;
});

const computedBackgroundColor = computed(
  () => props.backgroundColor ?? preset.value?.backgroundColor,
);

const computedIconColor = computed(() => props.iconColor ?? preset.value?.iconColor);

const computedIconBackgroundColor = computed(
  () => props.iconBackgroundColor ?? preset.value?.iconBackgroundColor,
);
</script>

<template>
  <div
    :class="[
      $style.root,
      {
        [$style.rounded]: computedRounded,
        [$style.withoutBackground]: !computedWithBackground,
        [$style.sizeM]: props.size === 'm',
      },
    ]"
    :style="{
      '--background-color': computedBackgroundColor,
      '--icon-color': computedIconColor,
      '--icon-background-color': computedIconBackgroundColor,
    }"
    v-bind="$attrs"
    data-ui-component="UiEmptyState"
  >
    <div v-if="computedWithIcon" :class="$style.icon">
      <slot name="icon">
        <IconLoader v-if="props.preset === 'loading'" :class="$style.spinner" />
        <IconCheck v-else-if="props.preset === 'success'" />
        <IconAlertTriangleFilled v-else-if="props.preset === 'warning'" />
        <IconAlertOctagonFilled v-else-if="props.preset === 'error'" />
        <IconSearch v-else-if="props.preset === 'search'" />
        <IconFile v-else />
      </slot>
    </div>
    <div
      v-if="hasSlotContent($slots.default) || hasSlotContent($slots.text)"
      :class="$style.textContainer"
    >
      <div v-if="hasSlotContent($slots.default)" :class="$style.title">
        <slot />
      </div>
      <div v-if="hasSlotContent($slots.text)" :class="$style.text">
        <slot name="text" />
      </div>
    </div>
    <div v-if="hasSlotContent($slots.content)" :class="$style.content">
      <slot name="content" />
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  --background-color: #f5f5f5;
  --icon-color: #734e7c;
  --icon-background-color: rgba(160, 120, 169, 0.12);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 20px;
  font-family: typography.$font-family-primary;
  color: #010000;
  text-align: center;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  background-color: var(--background-color);
  gap: 20px;
}

.rounded {
  border-radius: 20px;
}

.withoutBackground {
  background: none;
}

.icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  padding: 18px;
  box-sizing: border-box;
  color: var(--icon-color);
  background-color: var(--icon-background-color);

  > svg {
    width: 100%;
    height: 100%;
  }
}

.textContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.title {
  font-size: 22px;
  line-height: 24px;
  font-weight: 700;
  font-style: normal;
  letter-spacing: -0.66px;
  max-width: 360px;
}

.text {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  max-width: 360px;
  color: #655e60;
}

.content {
  display: flex;
  justify-content: center;
  width: 100%;
}

.spinner {
  animation: spinner 2s linear infinite;
}

@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.sizeM {
  gap: 20px;

  .textContainer {
    gap: 4px;
  }

  .title {
    font-size: 16px;
    line-height: 20px;
  }
}
</style>
